import React, { memo, useState } from 'react';
import { Accordion, Spacer } from '@components';
import {
  emailValidationForm,
  useAuthStore,
  useCheckoutStore,
  useErrorTrackerHandler,
  useLocationStore,
  useParams,
} from 'mycheck-core';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { ContactRow } from './components/ContactRow';
import styles from '../../OrderStatusPage.module.scss';
import { NotificationModal } from './components/NotificationModal';
import { UnsubscribeModal } from './components/UnsubscribeModal';
import { Form, Formik, FormikHelpers } from 'formik';
import { OrderGuestStatusType } from 'plugins/CheckoutPlugin/types/CheckoutTypes';

export enum NotificationType {
  SMS = 'Sms',
  EMAIL = 'Email',
}

export type ContactRowForm = {
  [key in NotificationType]: {
    value: string;
    maskedValue: string;
    isChecked: boolean;
  };
};

const notificationLabel = {
  [NotificationType.SMS]: 'SMS',
  [NotificationType.EMAIL]: 'Email',
};

interface OrderStatusNotificationsProps
  extends Pick<OrderGuestStatusType, 'maskedEmail' | 'maskedPhone'> {
  refetchOrderStatus: () => Promise<void>;
}

const OrderStatusNotifications: React.FC<OrderStatusNotificationsProps> = ({
  maskedEmail,
  maskedPhone,
  refetchOrderStatus,
}) => {
  const { t } = useTranslation();
  const [
    isChangeNotificationModalVisible,
    setIsChangeNotificationModalVisible,
  ] = useState(false);
  const [isUnsubscribeModalVisible, setIsUnsubscribeModalVisible] =
    useState(false);
  const [notificationType, setNotificationType] =
    useState<NotificationType | null>(null);

  const LocationStore = useLocationStore();
  const errorHandler = useErrorTrackerHandler();
  const AuthStore = useAuthStore();
  const CheckoutStore = useCheckoutStore();

  const { orderStatusId } = useParams<{ orderStatusId: string }>();

  const notificationEnabled = {
    [NotificationType.EMAIL]:
      LocationStore.guestOrderStatusEmailExperienceEnabled,
    [NotificationType.SMS]: LocationStore.guestOrderStatusSmsExperienceEnabled,
  };

  const initialValues: ContactRowForm = {
    Email: {
      value: '',
      maskedValue: maskedEmail,
      isChecked: !!maskedEmail,
    },
    Sms: {
      value: '',
      maskedValue: maskedPhone,
      isChecked: !!maskedPhone,
    },
  };

  const toggleNotificationModal = (
    notificationTypeValue?: NotificationType,
  ) => {
    setIsChangeNotificationModalVisible(!!notificationTypeValue);
    setNotificationType(notificationTypeValue ? notificationTypeValue : null);
  };

  const toggleUnsubscribeModal = () =>
    setIsUnsubscribeModalVisible((prevState) => !prevState);

  const handleCheckboxChange = (
    isChecked: boolean,
    notificationTypeValue: NotificationType,
  ) => {
    setNotificationType(notificationTypeValue);
    if (isChecked) {
      toggleUnsubscribeModal();
    } else {
      toggleNotificationModal(notificationTypeValue);
    }
  };

  const sendNotificationOpt = (value: string | null, isEnabled: boolean) =>
    CheckoutStore.setGuestOrderStatusNotificationOpt({
      id: orderStatusId,
      type: notificationType,
      isEnabled,
      accessToken: AuthStore.accessToken,
      data: value,
    });

  const triggerNotification = async (
    values?: ContactRowForm,
    isEnabled: boolean = true,
  ) => {
    try {
      await sendNotificationOpt(
        values ? values[notificationType].value : '',
        isEnabled,
      );
      await refetchOrderStatus();

      return true;
    } catch (err) {
      errorHandler.onError(err);

      return false;
    }
  };

  const onSubmit = async (
    values: ContactRowForm,
    formikProps: FormikHelpers<ContactRowForm>,
  ) => {
    if (isUnsubscribeModalVisible) {
      return;
    }
    const isSuccess = await triggerNotification(values);
    if (isSuccess) {
      formikProps.setFieldValue(`${notificationType}.isChecked`, true);
      toggleNotificationModal();
    }
  };

  const unsubscribe = async () => {
    const isSuccess = await triggerNotification(null, false);
    if (isSuccess) {
      toggleUnsubscribeModal();
    }
  };

  const getValidateSchema = () => {
    switch (notificationType) {
      case NotificationType.SMS:
        return yup.object().shape({
          [NotificationType.SMS]: yup.object().shape({
            value: yup.string().min(10).required(),
          }),
        });
      case NotificationType.EMAIL:
        return yup.object().shape({
          [NotificationType.EMAIL]: yup.object().shape({
            value: emailValidationForm(
              t('profile.emailValidationError.invalidEmail'),
            ).required(t('profile.emailValidationError.required')),
          }),
        });
      default:
        return;
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidateSchema()}
      onSubmit={(formValues, formikProps) => onSubmit(formValues, formikProps)}
      enableReinitialize
    >
      {({ resetForm }) => (
        <Form className={styles.orderStatusPageNotificationSection}>
          <Accordion
            title={
              <div className={styles.orderStatusPageNotificationSectionTitle}>
                {t('orderStatus.notification.title')}
              </div>
            }
            subtitle={
              <div
                className={styles.orderStatusPageNotificationSectionSubtitle}
              >
                {t('orderStatus.notification.subtitle')}
              </div>
            }
          >
            <div className={styles.orderStatusPageNotificationSection}>
              {Object.values(NotificationType).map((type) => (
                <ContactRow
                  key={type}
                  label={notificationLabel[type]}
                  notificationType={type}
                  onChange={handleCheckboxChange}
                  onUpdate={toggleNotificationModal}
                  enabled={notificationEnabled[type]}
                />
              ))}
              {isChangeNotificationModalVisible && (
                <NotificationModal
                  notificationType={notificationType}
                  close={() => {
                    toggleNotificationModal();
                    resetForm();
                  }}
                  label={notificationLabel[notificationType]}
                />
              )}
              {isUnsubscribeModalVisible && (
                <UnsubscribeModal
                  close={toggleUnsubscribeModal}
                  label={notificationLabel[notificationType]}
                  unsubscribe={unsubscribe}
                />
              )}
            </div>
          </Accordion>
          <Spacer color="#C6C6C6" />
        </Form>
      )}
    </Formik>
  );
};

export default memo(OrderStatusNotifications);
